import CloseIcon from '@mui/icons-material/Close'
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from '@mui/material'
import DialogActions from '@mui/material/DialogActions'
import Grid from '@mui/material/Grid2'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { Formik } from 'formik'
import { map } from 'lodash'
import React, { FC, useEffect } from 'react'
import { useAuth } from 'react-oidc-context'
import * as yup from 'yup'
import { useUserStore } from '../../../../store/storeUser'
import { useWrapperStore } from '../../../../store/storeWrapper'
import { DeletionReason } from '../../../../swagger/dto'


interface Props {
    open: boolean
    onClose: () => void
}

export const RemoveAccountDialog: FC<Props> = ({ open, onClose }) => {
    const auth = useAuth()
    const {
        deleteUser,
        isDoneDeleteUser,
        isLoadingDeleteUser,
        errorDeleteUser,
        setErrorDeleteUser,
    } = useUserStore()
    const { setStateNotification } = useWrapperStore()

    const [step, setStep] = React.useState(1)

    const handleClose = () => {
        onClose()
        setStep(1)
    }

    useEffect(() => {
        if (errorDeleteUser) {
            setStateNotification({
                isOpen: true,
                text: errorDeleteUser,
                isError: true,
            })
            setErrorDeleteUser(undefined)
        }
    }, [errorDeleteUser])

    useEffect(() => {
        if (isDoneDeleteUser) {
            auth.signoutRedirect()
        }
    }, [isDoneDeleteUser])

    const Content =
        step === 1 ? (
            <>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#5A5F72' }}>
                        Ваш аккаунт и все связанные данные будут безвозвратно удалены через 90 дней.
                        После этого срока, восстановить информацию будет невозможно.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} type="submit" fullWidth variant="outlined">
                        Отмена
                    </Button>
                    <Button
                        onClick={() => setStep(2)}
                        fullWidth
                        sx={{
                            color: '#EF4848',
                            background: '#F4F4F6',
                        }}>
                        Удалить
                    </Button>
                </DialogActions>
            </>
        ) : (
            <Formik
                initialValues={{
                    comment: '',
                    deletion_reason: DeletionReason.ValueДругаяПричинаУкажуВКомментарии,
                }}
                onSubmit={async (values) => {
                    await deleteUser(values)
                }}>
                {({
                    handleSubmit,
                    values,
                    handleBlur,
                    touched,
                    errors,
                    handleChange,
                    isValid,
                    dirty,
                }) => (
                    <form onSubmit={handleSubmit}>
                        <DialogContent sx={{ pb: 0 }}>
                            <Typography variant="caption1" sx={{ color: '#181B25' }}>
                                Укажите причину удаления
                            </Typography>
                            <Select
                                MenuProps={{
                                    anchorOrigin: {
                                        vertical: 'top',
                                        horizontal: 'center',
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                }}
                                sx={{ mr: 1, overflow: 'auto' }}
                                id="deletion_reason"
                                name="deletion_reason"
                                value={values.deletion_reason}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                color="secondary">
                                {map(DeletionReason, (value, index) => {
                                    return (
                                        <MenuItem
                                            value={value}
                                            key={value}
                                            sx={{
                                                borderBottom: '1px solid #F4F4F6',
                                                p: '10px 16px',
                                            }}>
                                            <Grid
                                                container
                                                alignItems="center"
                                                sx={{ width: '100%', m: 0 }}>
                                                <Typography variant="h6">{value}</Typography>
                                            </Grid>
                                        </MenuItem>
                                    )
                                })}
                            </Select>
                            <TextField
                                multiline
                                rows={6}
                                slotProps={{
                                    input: {
                                        sx: { borderRadius: '16px' },
                                    },
                                }}
                                sx={{ marginBottom: '16px' }}
                                margin="normal"
                                fullWidth
                                id="comment"
                                name="comment"
                                placeholder="Опишите причину удаления или свой опыт использования сервиса"
                                value={values.comment}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.comment && Boolean(errors.comment)}
                                helperText={touched.comment && errors.comment}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleClose}
                                disabled={isLoadingDeleteUser}>
                                Отмена
                            </Button>
                            <Button
                                sx={{
                                    color: '#EF4848',
                                    background: '#F4F4F6',
                                }}
                                disabled={!(isValid && dirty) || isLoadingDeleteUser}
                                type="submit"
                                fullWidth>
                                Подтвердить
                            </Button>
                        </DialogActions>
                    </form>
                )}
            </Formik>
        )

    return (
        <Dialog
            PaperProps={{
                style: {
                    width: '516px',
                },
            }}
            open={open}
            onClose={handleClose}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: '0',
                }}>
                Удаление аккаунта
                <IconButton
                    disabled={isLoadingDeleteUser}
                    onClick={handleClose}
                    sx={{
                        border: '1px solid #E9EAED',
                    }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            {Content}
        </Dialog>
    )
}
